/**
 * Optional "Why Suppliers Choose Antera" (or similar) differentiator grid.
 * Takes `whyChoose: { eyebrow?, heading?, intro?, cards: [{title, desc}] }`.
 * Icons and colours are position-mapped per card slot.
 */
import { motion } from "framer-motion";
import {
  ShieldCheck, Zap, Users, TrendingUp, Cog, Rocket, Award, Sparkles,
} from "lucide-react";

const CARD_META = [
  { Icon: ShieldCheck, bg: "bg-emerald-50", text: "text-emerald-600", ring: "ring-emerald-100" },
  { Icon: Zap,         bg: "bg-amber-50",   text: "text-amber-600",   ring: "ring-amber-100" },
  { Icon: Users,       bg: "bg-sky-50",     text: "text-sky-600",     ring: "ring-sky-100"   },
  { Icon: TrendingUp,  bg: "bg-violet-50",  text: "text-violet-600",  ring: "ring-violet-100" },
  { Icon: Cog,         bg: "bg-rose-50",    text: "text-rose-600",    ring: "ring-rose-100"   },
  { Icon: Rocket,      bg: "bg-indigo-50",  text: "text-indigo-600",  ring: "ring-indigo-100" },
  { Icon: Award,       bg: "bg-orange-50",  text: "text-orange-600",  ring: "ring-orange-100" },
  { Icon: Sparkles,    bg: "bg-teal-50",    text: "text-teal-600",    ring: "ring-teal-100"   },
];

export default function WhyChoose({ whyChoose }) {
  if (!whyChoose || !Array.isArray(whyChoose.cards) || whyChoose.cards.length === 0) return null;

  return (
    <section
      data-testid="solution-why-choose-section"
      className="py-14 lg:py-20 bg-white"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {(whyChoose.eyebrow || whyChoose.heading || whyChoose.intro) && (
          <div className="text-center max-w-3xl mx-auto mb-10 lg:mb-12">
            {whyChoose.eyebrow && (
              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-sky-600">
                {whyChoose.eyebrow}
              </span>
            )}
            {whyChoose.heading && (
              <h2
                data-testid="solution-why-choose-heading"
                className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight"
              >
                {whyChoose.heading}
              </h2>
            )}
            {whyChoose.intro && (
              <p className="mt-3 text-sm lg:text-base text-slate-500 leading-relaxed">
                {whyChoose.intro}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {whyChoose.cards.map((card, i) => {
            const meta = CARD_META[i % CARD_META.length];
            const Icon = meta.Icon;
            const slug = (card.title || `card-${i}`)
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "");
            return (
              <motion.div
                key={`${slug}-${i}`}
                data-testid={`solution-why-choose-card-${slug}`}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                className="bg-white border border-slate-200 rounded-2xl p-5 lg:p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <span className={`inline-flex w-11 h-11 rounded-xl ${meta.bg} ring-1 ${meta.ring} items-center justify-center mb-4`}>
                  <Icon className={`h-5 w-5 ${meta.text}`} strokeWidth={2.25} />
                </span>
                <h3 className="text-base lg:text-lg font-bold text-slate-900 mb-2 leading-snug">
                  {card.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {card.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
